<template>
  <div
    :class="[
      bem.b(),
      disabled ? bem.is('disabled') : '',
      hiddenBorder ? bem.is('hidden-border') : '',
    ]"
    :style="{ width: props.width + 'px', height: props.height + 'px' }"
  >
    <canvas ref="canvas"></canvas>

    <!-- 未签名时的占位符 -->
    <div
      v-if="!signatureExists && props.placeholder"
      :class="bem.e('placeholder')"
    >
      <p>{{ props.placeholder }}</p>
    </div>
  </div>
</template>

<script setup>
import { createNamespace } from '@ll-plus/utils'
import { nextTick, onMounted, onUnmounted, ref, watch } from 'vue'

import SignaturePad from 'signature_pad'

import { signatureProps } from './signature'

defineOptions({ name: 'LlSignature' })

const bem = createNamespace('signaturer')

const props = defineProps(signatureProps)

const canvas = ref(null)
const signaturePad = ref(null)

const signatureExists = ref(false) // 当前画布上是否有签名存在

// 监听宽高变化，重新初始化画布
watch(
  () => [props.width, props.height],
  () => {
    nextTick(() => {
      resizeCanvas()
    })
  }
)

// 监听线条样式变化
watch(
  () => [props.lineWidth, props.strokeStyle],
  () => {
    if (signaturePad.value) {
      signaturePad.value.minWidth = props.lineWidth
      signaturePad.value.maxWidth = props.lineWidth * 2
      signaturePad.value.penColor = props.strokeStyle
    }
  }
)

// 监听禁用状态变化
watch(
  () => props.disabled,
  (newVal) => {
    if (newVal) {
      disableSignaturePad()
    } else {
      enableSignaturePad()
    }
  }
)

// 初始化签名板
const initSignaturePad = () => {
  if (!canvas.value) return

  signaturePad.value = new SignaturePad(canvas.value, {
    minWidth: props.lineWidth,
    maxWidth: props.lineWidth * 2,
    penColor: props.strokeStyle,
    backgroundColor: 'rgb(255, 255, 255)',
    throttle: 8, // 节流控制，优化性能
    velocityFilterWeight: 0.8, // 速度过滤权重
  })

  // 设置画布尺寸
  resizeCanvas()

  // 监听签名开始事件
  signaturePad.value.addEventListener('beginStroke', () => {
    signatureExists.value = true
  })

  // 如果初始状态为禁用，则应用禁用逻辑
  if (props.disabled) {
    disableSignaturePad()
  }
}

// 调整画布大小
const resizeCanvas = () => {
  if (!canvas.value || !signaturePad.value) return

  const ratio = Math.max(window.devicePixelRatio || 1, 1)

  // 设置画布尺寸
  canvas.value.width = props.width * ratio
  canvas.value.height = props.height * ratio

  // 设置CSS尺寸
  canvas.value.style.width = `${props.width}px`
  canvas.value.style.height = `${props.height}px`

  // 缩放上下文
  const ctx = canvas.value.getContext('2d')
  ctx.scale(ratio, ratio)

  // 清除并重新绘制签名
  signaturePad.value.clear()
}

// 禁用签名板
const disableSignaturePad = () => {
  if (signaturePad.value) {
    // 移除所有事件监听
    signaturePad.value.off()
    // 设置画布为只读状态
    canvas.value.style.pointerEvents = 'none'
  }
}

// 启用签名板
const enableSignaturePad = () => {
  if (signaturePad.value) {
    // 重新附加事件监听
    signaturePad.value.on()
    // 恢复画布交互
    canvas.value.style.pointerEvents = 'auto'

    // 重新添加签名开始事件监听
    signaturePad.value.addEventListener('beginStroke', () => {
      signatureExists.value = true
    })
  }
}

// 清空签名
const clearSignature = () => {
  if (signaturePad.value) {
    signaturePad.value.clear()
    signatureExists.value = false
  }
}

// 保存签名为PNG
const saveSignature = () => {
  if (!signatureExists.value || !signaturePad.value || props.disabled)
    return null

  const dataURL = signaturePad.value.toDataURL('image/png')
  return dataURL
}

// 暴露方法给父组件
defineExpose({
  saveSignature,
  clearSignature,
  enableSignaturePad,
  disableSignaturePad,
})

onMounted(() => {
  initSignaturePad()
  window.addEventListener('resize', resizeCanvas)
})

onUnmounted(() => {
  window.removeEventListener('resize', resizeCanvas)
  if (signaturePad.value) {
    signaturePad.value.off()
  }
})
</script>
